import React, { useEffect, useMemo, useState } from 'react';
import { BsGear, BsPlay, BsRss, BsClipboard, BsArrowRepeat, BsThreeDotsVertical } from 'react-icons/bs';
import { apiFetch, parseJsonSafe } from '../utils/api';

const platforms = [
  { value: '', label: 'Todas las plataformas' },
  { value: 'freelancer', label: 'Freelancer' },
  { value: 'workana', label: 'Workana' },
  { value: 'peopleperhour', label: 'PeoplePerHour' }
];

const statuses = [
  { value: '', label: 'Todos los estados' },
  { value: 'nuevo', label: 'Nuevo' },
  { value: 'analizado', label: 'Analizado' },
  { value: 'descartado', label: 'Descartado' },
  { value: 'propuesta_generada', label: 'Propuesta generada' },
  { value: 'enviada', label: 'Enviada' },
  { value: 'ganado', label: 'Ganado' },
  { value: 'perdido', label: 'Perdido' }
];

const statusBadge = (status) => {
  switch (status) {
    case 'propuesta_generada':
      return 'badge bg-success';
    case 'analizado':
      return 'badge bg-info text-dark';
    case 'descartado':
      return 'badge bg-secondary';
    case 'enviada':
      return 'badge bg-primary';
    default:
      return 'badge bg-light text-dark';
  }
};

const formatBudget = (lead) => {
  if (!lead) return '-';
  const { budget_min: min, budget_max: max, currency, budget_type: type } = lead;
  if (min === null && max === null) return 'N/D';
  const fmt = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString());
  const symbol = currency || '';
  const range = min !== null && max !== null ? `${fmt(min)} - ${fmt(max)}` : fmt(min || max);
  return `${range} ${symbol} ${type || ''}`.trim();
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const LeadsPage = () => {
  const token = localStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [platformFilter, setPlatformFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [minFit, setMinFit] = useState('');
  const [minWin, setMinWin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({ poll: false, process: false, settings: false, proposal: false, status: false });
  const [detail, setDetail] = useState(null);
  const [settingsModal, setSettingsModal] = useState(false);
  const [settings, setSettings] = useState({
    auto_proposal_enabled: false,
    min_fit_score_for_proposal: 0.8,
    min_win_score_for_proposal: 0.5,
    min_budget: null,
    rss_cron_enabled: true,
    rss_cron_interval_minutes: 30,
    env_disabled: false,
    last_poll_at: null,
    last_process_at: null,
    last_poll_new_leads: null,
    last_process_analyzed: null,
    last_process_proposals: null,
    last_new_lead_at: null,
    alerts: [],
    cron_status: { running: false, nextRunAt: null, intervalMinutes: 30 }
  });
  const [sourceModal, setSourceModal] = useState({
    open: false,
    editing: null,
    form: { name: '', platform: 'freelancer', rssUrl: '', active: true, category: 'custom', spanishOnly: false }
  });
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  const categoryOptions = {
    freelancer: [
      { value: 'wordpress', label: 'WordPress', keyword: 'wordpress' },
      { value: 'woocommerce', label: 'WooCommerce', keyword: 'woocommerce' },
      { value: 'prestashop', label: 'PrestaShop', keyword: 'prestashop' },
      { value: 'api', label: 'API Integration', keyword: 'api integration' },
      { value: 'automation', label: 'Automation / Scripts', keyword: 'automation scripts' },
      { value: 'ai', label: 'AI / Machine Learning', keyword: 'machine learning' },
      { value: 'custom', label: 'Personalizada', keyword: '' }
    ],
    workana: [
      { value: 'it', label: 'IT & Programming', slug: 'it-programming' },
      { value: 'webdev', label: 'Web Development', slug: 'it-programming-web-development' },
      { value: 'apis', label: 'APIs & Integrations', slug: 'it-programming-api' },
      { value: 'ecommerce', label: 'E-commerce', slug: 'it-programming-ecommerce' },
      { value: 'custom', label: 'Personalizada', slug: '' }
    ],
    peopleperhour: [
      { value: 'wp', label: 'WordPress / WooCommerce', query: 'wordpress' },
      { value: 'webdev', label: 'Web Development', query: 'web development' },
      { value: 'scripts', label: 'Software & Scripts', query: 'software scripts' },
      { value: 'data', label: 'Data / APIs', query: 'api data' },
      { value: 'custom', label: 'Personalizada', query: '' }
    ]
  };

  const generateRssUrl = (platform, categoryValue) => {
    if (!platform || categoryValue === 'custom') return null;
    const opts = categoryOptions[platform] || [];
    const selected = opts.find((o) => o.value === categoryValue);
    if (!selected) return null;
    if (platform === 'freelancer') {
      const kw = encodeURIComponent(selected.keyword || '');
      // Freelancer usa rss.xml con query q
      return `https://www.freelancer.com/rss.xml?q=${kw}`;
    }
    if (platform === 'workana') {
      const slug = encodeURIComponent(selected.slug || 'it-programming');
      return `https://www.workana.com/jobs?category=${slug}&format=rss`;
    }
    if (platform === 'peopleperhour') {
      const q = encodeURIComponent(selected.query || '');
      return `https://www.peopleperhour.com/freelance-jobs?rss=1&query=${q}`;
    }
    return null;
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const fetchSettings = async () => {
    try {
      const res = await apiFetch('/api/leads/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok) {
        setSettings((prev) => ({
          ...prev,
          ...data,
          min_win_score_for_proposal: data.min_win_score_for_proposal ?? prev.min_win_score_for_proposal,
          rss_cron_enabled: data.rss_cron_enabled !== false,
          rss_cron_interval_minutes: data.rss_cron_interval_minutes || 30,
          alerts: data.alerts || [],
          cron_status: data.cron_status || { running: false },
          last_poll_at: data.last_poll_at || null,
          last_process_at: data.last_process_at || null,
          last_poll_new_leads: data.last_poll_new_leads ?? null,
          last_process_analyzed: data.last_process_analyzed ?? null,
          last_process_proposals: data.last_process_proposals ?? null,
          last_new_lead_at: data.last_new_lead_at || null
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSources = async () => {
    try {
      const res = await apiFetch('/api/leads/sources', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando fuentes');
      setSources(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (platformFilter) params.append('platform', platformFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (minFit) params.append('minFit', minFit);
      if (minWin) params.append('minWin', minWin);
      const res = await apiFetch(`/api/leads?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando oportunidades');
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, platformFilter, statusFilter, search, minFit, minWin]);

  useEffect(() => {
    fetchSources();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / pageSize), 1), [total, pageSize]);

  const handleAction = async (type) => {
    setActionLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const endpoint = type === 'poll' ? '/api/leads/poll-now' : '/api/leads/process-new';
      const res = await apiFetch(endpoint, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error ejecutando acción');
      alert(type === 'poll' ? `RSS leídos. Nuevos: ${data.totalNewLeads || 0}` : `Procesados ${data.analyzed || data.processed || 0} (propuestas ${data.proposalsGenerated || 0})`);
      fetchLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await apiFetch(`/api/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando lead');
      setDetail(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const copyProposal = (text) => navigator.clipboard.writeText(text || '');

  const updateStatus = async (status) => {
    if (!detail) return;
    setActionLoading((prev) => ({ ...prev, status: true }));
    try {
      const res = await apiFetch(`/api/leads/${detail.id}/status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar estado');
      setDetail(data);
      fetchLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, status: false }));
    }
  };

  const generateProposal = async () => {
    if (!detail) return;
    setActionLoading((prev) => ({ ...prev, proposal: true }));
    try {
      const res = await apiFetch('/api/leads/generate-proposal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: detail.id })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo generar propuesta');
      setDetail(data);
      fetchLeads();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, proposal: false }));
    }
  };

  const saveSettings = async () => {
    setActionLoading((prev) => ({ ...prev, settings: true }));
    try {
      const interval = Math.min(Math.max(Number(settings.rss_cron_interval_minutes) || 30, 5), 60);
      const res = await apiFetch('/api/leads/settings', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoProposalEnabled: settings.auto_proposal_enabled,
          minFitScoreForProposal: settings.min_fit_score_for_proposal,
          minWinScoreForProposal: settings.min_win_score_for_proposal,
          minBudget: settings.min_budget,
          rssCronEnabled: settings.rss_cron_enabled,
          rssCronIntervalMinutes: interval
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setSettings((prev) => ({
        ...prev,
        ...data,
        rss_cron_enabled: data.rss_cron_enabled !== false,
        rss_cron_interval_minutes: data.rss_cron_interval_minutes || interval
      }));
      setSettingsModal(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, settings: false }));
    }
  };

  const openSource = (source = null) => {
    setSourceModal({
      open: true,
      editing: source,
      form: source
        ? {
            name: source.name || '',
            platform: source.platform || 'freelancer',
            rssUrl: source.rss_url || source.rssUrl || '',
            active: Boolean(source.active),
            category: source.category || 'custom',
            spanishOnly: Boolean(source.spanish_only)
          }
        : { name: '', platform: 'freelancer', rssUrl: '', active: true, category: 'custom', spanishOnly: false }
    });
  };

  const saveSource = async () => {
    const { editing, form } = sourceModal;
    if (!form.name || !form.platform || !form.rssUrl) {
      return alert('Nombre, plataforma y URL RSS son obligatorios');
    }
    setActionLoading((prev) => ({ ...prev, source: true }));
    try {
      const endpoint = editing ? `/api/leads/sources/${editing.id}` : '/api/leads/sources';
      const method = editing ? 'PUT' : 'POST';
      const res = await apiFetch(endpoint, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          platform: form.platform,
          rssUrl: form.rssUrl,
          active: form.active,
          spanishOnly: form.spanishOnly
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la fuente');
      setSourceModal({
        open: false,
        editing: null,
        form: { name: '', platform: 'freelancer', rssUrl: '', active: true, category: 'custom', spanishOnly: false }
      });
      fetchSources();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading((prev) => ({ ...prev, source: false }));
    }
  };

  const deleteSource = async (id) => {
    if (!window.confirm('¿Eliminar esta fuente RSS?')) return;
    try {
      const res = await apiFetch(`/api/leads/sources/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      fetchSources();
    } catch (err) {
      setError(err.message);
    }
  };

  const proposalQuestions = useMemo(() => {
    if (!detail) return [];
    const q = detail.ia_proposal_questions;
    if (Array.isArray(q)) return q;
    if (typeof q === 'string') {
      try {
        const parsed = JSON.parse(q);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_err) {
        return [];
      }
    }
    return [];
  }, [detail]);

  return (
    <div className="small" style={{ fontSize: '0.9rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Captación</h2>
        {isMobile ? (
          <div className="position-relative">
            <button className="btn btn-outline-secondary" onClick={() => setActionsMenuOpen((o) => !o)}>
              <BsThreeDotsVertical />
            </button>
            {actionsMenuOpen && (
              <div className="dropdown-menu dropdown-menu-end show" style={{ minWidth: '220px', right: 0, left: 'auto' }}>
                <button className="dropdown-item d-flex align-items-center gap-2" disabled={actionLoading.poll} onClick={() => { handleAction('poll'); setActionsMenuOpen(false); }}>
                  <BsRss /> {actionLoading.poll ? 'Leyendo RSS...' : 'Leer RSS ahora'}
                </button>
                <button className="dropdown-item d-flex align-items-center gap-2" disabled={actionLoading.process} onClick={() => { handleAction('process'); setActionsMenuOpen(false); }}>
                  <BsPlay /> {actionLoading.process ? 'Procesando IA...' : 'Procesar nuevas ofertas (IA)'}
                </button>
                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => { fetchSettings(); setSettingsModal(true); setActionsMenuOpen(false); }}>
                  <BsGear /> Configuración
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2" disabled={actionLoading.poll} onClick={() => handleAction('poll')}>
              <BsRss /> {actionLoading.poll ? 'Leyendo RSS...' : 'Leer RSS ahora'}
            </button>
            <button className="btn btn-outline-primary d-flex align-items-center gap-2" disabled={actionLoading.process} onClick={() => handleAction('process')}>
              <BsPlay /> {actionLoading.process ? 'Procesando IA...' : 'Procesar nuevas ofertas (IA)'}
            </button>
            <button className="btn btn-outline-secondary" onClick={() => { fetchSettings(); setSettingsModal(true); }}>
              <BsGear /> Configuración
            </button>
          </div>
        )}
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            <div className="col-md-3">
              <select className="form-select" value={platformFilter} onChange={(e) => { setPage(1); setPlatformFilter(e.target.value); }}>
                {platforms.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}>
                {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                placeholder="Encaje min (0-1)"
                value={minFit}
                min="0"
                max="1"
                step="0.05"
                onChange={(e) => { setPage(1); setMinFit(e.target.value); }}
              />
            </div>
            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                placeholder="Prob. ganar (0-1)"
                value={minWin}
                min="0"
                max="1"
                step="0.05"
                onChange={(e) => { setPage(1); setMinWin(e.target.value); }}
              />
            </div>
            <div className="col-md-3">
              <input className="form-control" placeholder="Buscar por título o descripción" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Fuentes RSS</h5>
            <button className="btn btn-sm btn-outline-primary" onClick={() => openSource()}>Añadir fuente</button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Plataforma</th>
                  <th>URL</th>
                  <th>Activa</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sources.length ? sources.map((s) => (
                  <tr key={s.id}>
                    <td>{s.name}</td>
                    <td className="text-capitalize">{s.platform}</td>
                    <td className="text-truncate" style={{ maxWidth: 260 }} title={s.rss_url || s.rssUrl}>{s.rss_url || s.rssUrl}</td>
                    <td>
                      {s.active ? 'Sí' : 'No'} {s.spanish_only ? <span className="badge bg-light text-dark ms-1">ES</span> : null}
                    </td>
                    <td className="text-end">
                      <button className="btn btn-outline-secondary btn-sm me-2" onClick={() => openSource(s)}>Editar</button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => deleteSource(s.id)}>Eliminar</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5">Sin fuentes. Añade al menos una para leer ofertas.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <p>Cargando...</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Plataforma</th>
                    <th>Título</th>
                    <th>Encaje IA</th>
                    <th>Prob. ganar</th>
                    <th>Presupuesto</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? items.map((lead) => (
                    <tr key={lead.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(lead.id)}>
                      <td>{lead.published_at ? new Date(lead.published_at).toLocaleString() : '-'}</td>
                      <td className="text-capitalize">{lead.platform}</td>
                      <td className="text-truncate" style={{ maxWidth: 260 }}>
                        {lead.title}
                        {lead.status === 'propuesta_generada' && <span className="ms-2 badge bg-success">IA</span>}
                      </td>
                      <td>{lead.ia_fit_score !== null && lead.ia_fit_score !== undefined ? `${Math.round((lead.ia_fit_score || 0) * 100)}%` : '-'}</td>
                      <td>{lead.ia_win_score !== null && lead.ia_win_score !== undefined ? `${Math.round((lead.ia_win_score || 0) * 100)}%` : '-'}</td>
                      <td>{formatBudget(lead)}</td>
                      <td><span className={statusBadge(lead.status)}>{lead.status}</span></td>
                    </tr>
                  )) : (
                    <tr><td colSpan="7">Sin oportunidades.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>Página {page} de {totalPages} (total {total})</div>
            <div className="d-flex gap-2">
              <select className="form-select form-select-sm" style={{ width: 100 }} value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n} / pág</option>)}
              </select>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </div>
        </div>
      </div>

      {detail && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{detail.title}</h5>
                <button className="btn-close" onClick={() => setDetail(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-2 text-muted small">{detail.platform} · {detail.url && <a href={detail.url} target="_blank" rel="noreferrer">Abrir</a>}</div>
                <p style={{ whiteSpace: 'pre-wrap' }}>{detail.description}</p>
                <div className="mb-3">
                  <strong>Presupuesto:</strong> {formatBudget(detail)}
                </div>
                <div className="mb-3">
                  <strong>Estado:</strong> <span className={statusBadge(detail.status)}>{detail.status}</span>
                </div>

                <h6>Análisis IA</h6>
                <ul className="small">
                  <li><strong>Intent:</strong> {detail.ia_intent || '-'}</li>
                  <li><strong>Fit:</strong> {detail.ia_fit_score !== null && detail.ia_fit_score !== undefined ? `${Math.round((detail.ia_fit_score || 0) * 100)}%` : '-'}</li>
                  <li><strong>Complejidad:</strong> {detail.ia_complexity_score !== null && detail.ia_complexity_score !== undefined ? `${Math.round((detail.ia_complexity_score || 0) * 100)}%` : '-'}</li>
                  <li><strong>Prob. ganar:</strong> {detail.ia_win_score !== null && detail.ia_win_score !== undefined ? `${Math.round((detail.ia_win_score || 0) * 100)}%` : '-'}</li>
                  <li><strong>Resumen:</strong> {detail.ia_summary || '-'}</li>
                  <li><strong>Recomendación:</strong> {detail.ia_recommendation || '-'}</li>
                </ul>

                    {detail.ia_proposal_body && (
                      <div className="mt-3">
                        <h6>Propuesta IA</h6>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div><strong>{detail.ia_proposal_title}</strong></div>
                        <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={() => copyProposal(detail.ia_proposal_body)}>
                          <BsClipboard /> Copiar
                        </button>
                      </div>
                    <div className="p-2 border rounded" style={{ whiteSpace: 'pre-wrap', maxHeight: 260, overflowY: 'auto' }}>
                      {detail.ia_proposal_body}
                    </div>
                    {proposalQuestions.length > 0 && (
                      <div className="mt-2">
                        <strong>Preguntas:</strong>
                        <ul className="small">
                          {proposalQuestions.map((q, idx) => <li key={idx}>{q}</li>)}
                        </ul>
                      </div>
                    )}
                    {detail.ia_proposal_budget_note && <div className="small mt-2"><strong>Nota presupuesto:</strong> {detail.ia_proposal_budget_note}</div>}
                    {detail.ia_proposal_delivery_estimate && <div className="small"><strong>Entrega:</strong> {detail.ia_proposal_delivery_estimate}</div>}
                  </div>
                )}
              </div>
              <div className="modal-footer d-flex flex-wrap gap-2 justify-content-between">
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setDetail(null)}>Cerrar</button>
                  <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" disabled={actionLoading.proposal} onClick={generateProposal}>
                    {actionLoading.proposal ? <BsArrowRepeat className="spin" /> : <BsPlay />} Generar propuesta IA
                  </button>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-success btn-sm" disabled={actionLoading.status} onClick={() => updateStatus('enviada')}>Marcar enviada</button>
                  <button className="btn btn-outline-secondary btn-sm" disabled={actionLoading.status} onClick={() => updateStatus('descartado')}>Descartar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {sourceModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{sourceModal.editing ? 'Editar fuente' : 'Añadir fuente'}</h5>
                <button className="btn-close" onClick={() => setSourceModal({ open: false, editing: null, form: { name: '', platform: 'freelancer', rssUrl: '', active: true, category: 'custom' } })}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nombre</label>
                  <input
                    className="form-control"
                    value={sourceModal.form.name}
                    onChange={(e) => setSourceModal((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Plataforma</label>
                  <select
                    className="form-select"
                    value={sourceModal.form.platform}
                    onChange={(e) => {
                      const newPlatform = e.target.value;
                      const options = categoryOptions[newPlatform] || [];
                      const defaultCat = options.find((o) => o.value === 'custom') ? 'custom' : (options[0]?.value || 'custom');
                      setSourceModal((prev) => ({
                        ...prev,
                        form: {
                          ...prev.form,
                          platform: newPlatform,
                          category: defaultCat,
                          rssUrl: defaultCat === 'custom' ? prev.form.rssUrl : generateRssUrl(newPlatform, defaultCat) || ''
                        }
                      }));
                    }}
                  >
                    {platforms.filter((p) => p.value).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Categoría sugerida</label>
                  <select
                    className="form-select"
                    value={sourceModal.form.category || 'custom'}
                    onChange={(e) => {
                      const newCat = e.target.value;
                      const autoUrl = generateRssUrl(sourceModal.form.platform, newCat);
                      setSourceModal((prev) => ({
                        ...prev,
                        form: {
                          ...prev.form,
                          category: newCat,
                          rssUrl: newCat === 'custom' ? prev.form.rssUrl : autoUrl || ''
                        }
                      }));
                    }}
                  >
                    {(categoryOptions[sourceModal.form.platform] || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div className="form-text">Selecciona categoría para autogenerar la URL RSS, o "Personalizada" para pegar tu URL.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">URL RSS</label>
                  <input
                    className="form-control"
                    value={sourceModal.form.rssUrl}
                    onChange={(e) => setSourceModal((prev) => ({ ...prev, form: { ...prev.form, rssUrl: e.target.value } }))}
                  />
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sourceActive"
                    checked={sourceModal.form.active}
                    onChange={(e) => setSourceModal((prev) => ({ ...prev, form: { ...prev.form, active: e.target.checked } }))}
                  />
                  <label className="form-check-label" htmlFor="sourceActive">Activa</label>
                </div>
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="spanishOnly"
                    checked={sourceModal.form.spanishOnly}
                    onChange={(e) => setSourceModal((prev) => ({ ...prev, form: { ...prev.form, spanishOnly: e.target.checked } }))}
                  />
                  <label className="form-check-label" htmlFor="spanishOnly">Solo ofertas en español (heurístico)</label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setSourceModal({
                      open: false,
                      editing: null,
                      form: { name: '', platform: 'freelancer', rssUrl: '', active: true, category: 'custom', spanishOnly: false }
                    })
                  }
                >
                  Cancelar
                </button>
                <button className="btn btn-primary" disabled={actionLoading.source} onClick={saveSource}>
                  {actionLoading.source ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {settingsModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Configuración</h5>
                <button className="btn-close" onClick={() => setSettingsModal(false)}></button>
              </div>
              <div className="modal-body">
                {settings.env_disabled && (
                  <div className="alert alert-warning">
                    El cron está deshabilitado por variable de entorno LEADS_CRON_ENABLED=false. Cambia el entorno para permitir programarlo.
                  </div>
                )}
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="rssCron"
                    checked={Boolean(settings.rss_cron_enabled)}
                    disabled={settings.env_disabled}
                    onChange={(e) => setSettings((prev) => ({ ...prev, rss_cron_enabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="rssCron">Leer RSS automáticamente</label>
                </div>
                <div className="mb-4">
                  <label className="form-label">Intervalo (minutos)</label>
                  <input
                    type="number"
                    className="form-control"
                    min="5"
                    max="60"
                    step="5"
                    disabled={settings.env_disabled}
                    value={settings.rss_cron_interval_minutes ?? 30}
                    onChange={(e) => {
                      const v = Math.min(Math.max(Number(e.target.value) || 0, 5), 60);
                      setSettings((prev) => ({ ...prev, rss_cron_interval_minutes: v }));
                    }}
                  />
                  <div className="form-text">Entre 5 y 60 minutos. El cron se reprograma al guardar.</div>
                </div>
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      Estado cron:{' '}
                      <span className={`badge ${settings.cron_status?.running ? 'bg-success' : 'bg-secondary'}`}>
                        {settings.cron_status?.running ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="text-muted small">
                      Próximo run: {settings.cron_status?.nextRunAt ? formatDateTime(settings.cron_status.nextRunAt) : '—'}
                    </div>
                  </div>
                  <ul className="small mt-2 mb-0">
                    <li>Último poll: {formatDateTime(settings.last_poll_at)} (nuevos: {settings.last_poll_new_leads ?? 0})</li>
                    <li>
                      Último proceso IA: {formatDateTime(settings.last_process_at)} (analizados: {settings.last_process_analyzed ?? 0}, propuestas: {settings.last_process_proposals ?? 0})
                    </li>
                    <li>Último lead nuevo: {formatDateTime(settings.last_new_lead_at)}</li>
                  </ul>
                  {settings.alerts?.length ? (
                    <div className="alert alert-warning small mt-2 mb-0">
                      {settings.alerts.map((a, idx) => <div key={idx}>• {a}</div>)}
                    </div>
                  ) : null}
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="autoProposal"
                    checked={Boolean(settings.auto_proposal_enabled)}
                    onChange={(e) => setSettings((prev) => ({ ...prev, auto_proposal_enabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="autoProposal">Generar propuesta automáticamente</label>
                </div>
                <div className="mb-3">
                  <label className="form-label">Encaje mínimo (0-1)</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.min_fit_score_for_proposal ?? 0.8}
                    onChange={(e) => setSettings((prev) => ({ ...prev, min_fit_score_for_proposal: Number(e.target.value) }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Prob. ganar mínima (0-1)</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="1"
                    step="0.05"
                    value={settings.min_win_score_for_proposal ?? 0.5}
                    onChange={(e) => setSettings((prev) => ({ ...prev, min_win_score_for_proposal: Number(e.target.value) }))}
                  />
                  <div className="form-text">La propuesta automática solo se genera si encaje y prob. ganar superan estos umbrales.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Presupuesto mínimo (opcional)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.min_budget ?? ''}
                    onChange={(e) => setSettings((prev) => ({ ...prev, min_budget: e.target.value === '' ? null : Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setSettingsModal(false)}>Cancelar</button>
                <button className="btn btn-primary" disabled={actionLoading.settings} onClick={saveSettings}>
                  {actionLoading.settings ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;
